import React, { useCallback, useEffect, useState } from 'react';
import LoadingSpinner from '../../components/ui/LoadingSpinner';
import adsApi from './adsApi';
import AdsImageField from './AdsImageField.jsx';

const empty = {
  title: '',
  slug: '',
  advertiser_id: '',
  hero_banner_url: '',
  brand_logo_url: '',
  offer_details: '',
  coupon_code: '',
  buy_now_url: '',
  buy_now_text: 'Buy Now',
  product_features: '[]',
  faq: '[]',
  is_published: false,
};

export default function AdsLandingPagesPage() {
  const [items, setItems] = useState([]);
  const [advertisers, setAdvertisers] = useState([]);
  const [form, setForm] = useState(empty);
  const [editingId, setEditingId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [lp, adv] = await Promise.all([
        adsApi.listLandingPages({ limit: 200 }),
        adsApi.listAdvertisers({ limit: 500 }),
      ]);
      setItems(lp?.data?.items || []);
      setAdvertisers(adv?.data?.items || []);
    } catch (err) {
      setError(err.message || 'Failed to load');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError('');
    try {
      const payload = {
        ...form,
        advertiser_id: Number(form.advertiser_id),
        product_features: JSON.parse(form.product_features || '[]'),
        faq: JSON.parse(form.faq || '[]'),
      };
      if (editingId) await adsApi.updateLandingPage(editingId, payload);
      else await adsApi.createLandingPage(payload);
      setForm(empty);
      setEditingId(null);
      await load();
    } catch (err) {
      setError(err.message || 'Save failed');
    }
  };

  if (loading) return <LoadingSpinner size="large" message="Loading landing pages..." />;

  return (
    <div className="space-y-6 p-4 md:p-6">
      <h1 className="text-2xl font-bold text-slate-900">Brand Landing Pages</h1>
      {error ? (
        <div className="rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{error}</div>
      ) : null}

      <form onSubmit={onSubmit} className="grid items-start gap-3 rounded-lg border border-slate-200 bg-white p-4 md:grid-cols-2">
        <input
          className="h-10 rounded border border-slate-300 px-3 py-2 text-sm"
          placeholder="Title *"
          required
          value={form.title}
          onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
        />
        <input
          className="h-10 rounded border border-slate-300 px-3 py-2 text-sm"
          placeholder="Slug"
          value={form.slug}
          onChange={(e) => setForm((f) => ({ ...f, slug: e.target.value }))}
        />
        <select
          className="h-10 self-start rounded border border-slate-300 px-3 py-2 text-sm"
          required
          value={form.advertiser_id}
          onChange={(e) => setForm((f) => ({ ...f, advertiser_id: e.target.value }))}
        >
          <option value="">Advertiser *</option>
          {advertisers.map((a) => (
            <option key={a.id} value={a.id}>
              {a.name}
            </option>
          ))}
        </select>
        <input
          className="h-10 self-start rounded border border-slate-300 px-3 py-2 text-sm"
          placeholder="Coupon code"
          value={form.coupon_code}
          onChange={(e) => setForm((f) => ({ ...f, coupon_code: e.target.value }))}
        />
        <AdsImageField
          label="Hero banner"
          value={form.hero_banner_url}
          onChange={(path) => setForm((f) => ({ ...f, hero_banner_url: path }))}
          onError={(msg) => setError(msg)}
        />
        <AdsImageField
          label="Brand logo"
          value={form.brand_logo_url}
          onChange={(path) => setForm((f) => ({ ...f, brand_logo_url: path }))}
          onError={(msg) => setError(msg)}
        />
        <input
          className="h-10 rounded border border-slate-300 px-3 py-2 text-sm"
          placeholder="Buy now URL"
          value={form.buy_now_url}
          onChange={(e) => setForm((f) => ({ ...f, buy_now_url: e.target.value }))}
        />
        <input
          className="h-10 rounded border border-slate-300 px-3 py-2 text-sm"
          placeholder="Buy now button text"
          value={form.buy_now_text}
          onChange={(e) => setForm((f) => ({ ...f, buy_now_text: e.target.value }))}
        />
        <textarea
          className="rounded border border-slate-300 px-3 py-2 text-sm md:col-span-2"
          placeholder="Offer details"
          rows={3}
          value={form.offer_details}
          onChange={(e) => setForm((f) => ({ ...f, offer_details: e.target.value }))}
        />
        <textarea
          className="rounded border border-slate-300 px-3 py-2 text-sm md:col-span-2 font-mono text-xs"
          placeholder='Product features JSON e.g. ["Feature 1"]'
          rows={2}
          value={form.product_features}
          onChange={(e) => setForm((f) => ({ ...f, product_features: e.target.value }))}
        />
        <textarea
          className="rounded border border-slate-300 px-3 py-2 text-sm md:col-span-2 font-mono text-xs"
          placeholder='FAQ JSON e.g. [{"q":"...","a":"..."}]'
          rows={2}
          value={form.faq}
          onChange={(e) => setForm((f) => ({ ...f, faq: e.target.value }))}
        />
        <label className="flex h-10 items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={form.is_published}
            onChange={(e) => setForm((f) => ({ ...f, is_published: e.target.checked }))}
          />
          Published
        </label>
        <button type="submit" className="h-10 rounded-md bg-slate-900 px-4 py-2 text-sm font-medium text-white">
          {editingId ? 'Update page' : 'Create page'}
        </button>
      </form>

      <div className="overflow-x-auto rounded-lg border border-slate-200 bg-white">
        <table className="min-w-full text-left text-sm">
          <thead className="bg-slate-50 text-xs uppercase text-slate-500">
            <tr>
              <th className="px-3 py-2">Title</th>
              <th className="px-3 py-2">Slug</th>
              <th className="px-3 py-2">Advertiser</th>
              <th className="px-3 py-2">Published</th>
              <th className="px-3 py-2">Actions</th>
            </tr>
          </thead>
          <tbody>
            {items.map((row) => (
              <tr key={row.id} className="border-t border-slate-100">
                <td className="px-3 py-2 font-medium">{row.title}</td>
                <td className="px-3 py-2 font-mono text-xs">
                  <a className="text-blue-700 hover:underline" href={`/ads/${row.slug}`} target="_blank" rel="noreferrer">
                    {row.slug}
                  </a>
                </td>
                <td className="px-3 py-2">{row.advertiser_name}</td>
                <td className="px-3 py-2">{row.is_published ? 'Yes' : 'No'}</td>
                <td className="px-3 py-2 space-x-2">
                  <button
                    type="button"
                    className="text-blue-700 hover:underline"
                    onClick={() => {
                      setEditingId(row.id);
                      setForm({
                        title: row.title || '',
                        slug: row.slug || '',
                        advertiser_id: String(row.advertiser_id || ''),
                        hero_banner_url: row.hero_banner_url || '',
                        brand_logo_url: row.brand_logo_url || '',
                        offer_details: row.offer_details || '',
                        coupon_code: row.coupon_code || '',
                        buy_now_url: row.buy_now_url || '',
                        buy_now_text: row.buy_now_text || 'Buy Now',
                        product_features: JSON.stringify(row.product_features || []),
                        faq: JSON.stringify(row.faq || []),
                        is_published: !!row.is_published,
                      });
                    }}
                  >
                    Edit
                  </button>
                  <button
                    type="button"
                    className="text-red-600 hover:underline"
                    onClick={async () => {
                      if (!window.confirm('Delete landing page?')) return;
                      await adsApi.deleteLandingPage(row.id);
                      await load();
                    }}
                  >
                    Delete
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
